import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  SimpleGrid,
  Divider,
  Button,
  useToast,
  Badge,
  InputGroup,
  InputLeftAddon,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
} from '@chakra-ui/react';
import { DollarSign, Send, History, CreditCard, Gift, TrendingUp, TrendingDown, Shield } from 'lucide-react';
import { auth } from '../services/firebase';
import { Capacitor } from '@capacitor/core';
import walletService from '../services/walletService';
import transactionService from '../services/transactionService';
import PokerButton from '../components/ui/PokerButton';
import PokerCard from '../components/ui/PokerCard';
import { POKER_COLORS } from '../theme/designSystem';
import TransferModal from '../components/TransferModal';
import { KYCVerificationModal, GeoBlockedModal, AMOEClaimCard } from '../components/compliance';
import { geoService } from '../services/geoService';
import { complianceService } from '../services/complianceService';
import { KYCStatus, GeoBlockReason } from '../types/complianceTypes';
import { usePaymentsHold } from '../services/paymentsHoldService';
import gameApiService from '../services/gameApiService';
import { API_BASE } from '../utils/apiBase';
import { isStoreSafe } from '../utils/storeSafe';
import kycService from '../services/kycService';

//  iOS FIX: Get proper URL for Stripe redirects

// On native platforms, window.location.origin returns capacitor://localhost which Stripe rejects
const getStripeRedirectUrl = (path: string) => {
  if (Capacitor.isNativePlatform()) {
    return `https://pokersweepstars.web.app${path}`;
  }
  return `${window.location.origin}${path}`;
};

//  Tutorial: data-tutorial attributes added for spotlight highlighting


// Type definitions
interface WalletBalance {
  playCoins: number;
  pkCash: number;
  pkCashPlaythrough?: number; //  Amount that still needs to be wagered

  pkCashHeld?: number; //  Amount held for pending withdrawals

  totalPlayCoinsWon?: number;
  totalPlayCoinsLost?: number;
  totalPKCashWon?: number;
  totalPKCashLost?: number;
  gamesPlayed?: number;
  rakeBalance?: number; //  ENTERPRISE: Separate agent commission balance

}

interface Transaction {
  id: string;
  odD: string;
  type: 'deposit' | 'withdrawal' | 'game_win' | 'game_loss' | 'daily_bonus' | 'purchase' | 'rake_earned';
  currency?: 'playcoins' | 'pkcash';
  amount: number;
  status?: 'pending' | 'completed' | 'failed';
  timestamp?: any;
  createdAt?: string;
  description?: string;
}

const WalletPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const currentUser = auth.currentUser;

  const initialWallet: WalletBalance = {
    playCoins: 0,
    pkCash: 0,
  };

  const [wallet, setWallet] = useState<WalletBalance>(initialWallet);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [allTransactions, setAllTransactions] = useState<Transaction[]>([]);
  const [transactionFilter, setTransactionFilter] = useState<string>('all');
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [loadingAllTransactions, setLoadingAllTransactions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const paymentsHold = usePaymentsHold();
  const cashOutsPaused = paymentsHold.enabled && paymentsHold.blockCashOuts;
  const [selectedCurrency, setSelectedCurrency] = useState<'playcoins' | 'pkcash'>('pkcash');
  const [processing, setProcessing] = useState(false);

  //  NEW: Compliance state

  const { isOpen: isKYCOpen, onOpen: onKYCOpen, onClose: onKYCClose } = useDisclosure();
  const { isOpen: isGeoBlockedOpen, onOpen: onGeoBlockedOpen, onClose: onGeoBlockedClose } = useDisclosure();
  const [kycStatus, setKycStatus] = useState<KYCStatus>('none');
  const [geoBlockReason, setGeoBlockReason] = useState<GeoBlockReason | null>(null);
  const [geoLocation, setGeoLocation] = useState<{ city?: string; region?: string; country?: string } | null>(null);

  //  Connect account state for payouts

  const [connectStatus, setConnectStatus] = useState<{
    hasAccount: boolean;
    payoutsEnabled: boolean;
    loading: boolean;
    accountStatus?: string;
    ageRestricted?: boolean;
    //  NEW: Payout method availability

    payoutMethods?: {
      hasBankAccount: boolean;
      hasDebitCard: boolean;
      instantPayoutEligible: boolean;
      standardPayoutEligible: boolean;
    };
  }>({ hasAccount: false, payoutsEnabled: false, loading: true });
  // Payout destination (no processor): USDC wallet or PayPal.
  type PayoutMethod = 'usdc' | 'paypal';
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod>('paypal');
  const [payoutDests, setPayoutDests] = useState<Record<string, { value: string; network: string | null; holdUntil: string | null }>>({});
  const [usdcPayoutsEnabled, setUsdcPayoutsEnabled] = useState(false);
  const [usdcNetwork, setUsdcNetwork] = useState('');
  const usdcNetworkLabel = ({ usdcmatic: 'USDC on Polygon', usdcsol: 'USDC on Solana', usdcbase: 'USDC on Base', usdcarb: 'USDC on Arbitrum', usdc: 'USDC on Ethereum', usdcerc20: 'USDC on Ethereum' } as Record<string, string>)[usdcNetwork] || (usdcNetwork ? `USDC (${usdcNetwork})` : 'USDC');
  const [destInput, setDestInput] = useState('');
  const [savingDest, setSavingDest] = useState(false);
  // Crypto stays out of the store apps entirely (Apple / Google Play rules).
  const isNativeApp = Capacitor.isNativePlatform();
  const loadPayoutDestinations = async () => {
    const r = await gameApiService.payoutDestinations();
    if (!r.success) return;
    setPayoutDests(r.destinations || {});
    setUsdcPayoutsEnabled(!!r.usdcPayoutsEnabled && !isNativeApp);
    setUsdcNetwork(r.usdcNetwork || '');
    const have = (Object.keys(r.destinations || {}) as PayoutMethod[]).filter(m => m !== 'usdc' || (!!r.usdcPayoutsEnabled && !isNativeApp));
    if (have.length && !have.includes(payoutMethod)) setPayoutMethod(have[0]);
  };
  const savePayoutDestination = async () => {
    const value = destInput.trim();
    if (!value) return;
    setSavingDest(true);
    const r = await gameApiService.setPayoutDestination(payoutMethod, value);
    setSavingDest(false);
    if (!r.success) { toast({ title: 'Could not save', description: r.error, status: 'error', duration: 4000 }); return; }
    setDestInput('');
    toast({ title: 'Saved', description: r.holdUntil ? `New wallet addresses can receive redemptions after ${new Date(r.holdUntil).toLocaleString()}.` : undefined, status: 'success', duration: 4000 });
    await loadPayoutDestinations();
  };
  const [settingUpConnect, setSettingUpConnect] = useState(false);
  
  //  NEW: Transfer modal state

  const [showTransferModal, setShowTransferModal] = useState(false);
  
  //  NEW: Verification polling state

  const [verificationPolling, setVerificationPolling] = useState(false);

  useEffect(() => {
    if (currentUser?.uid) {
      loadWalletData();
      loadKYCStatus();
      checkGeoLocation();
      loadPayoutDestinations();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  //  NEW: Handle return from Stripe Connect onboarding

  useEffect(() => {
    const connectParam = searchParams.get('connect');
    
    if (connectParam === 'complete' && currentUser?.uid) {
      // Clear the URL param
      setSearchParams({});
      
      // Start polling for verification status
      toast({
        title: '⏳ Verifying your account...',
        description: 'This usually takes just a few seconds.',
        status: 'info',
        duration: 5000,
      });
      
      setVerificationPolling(true);
      pollConnectStatus();
    } else if (connectParam === 'refresh' && currentUser?.uid) {
      // User needs to complete onboarding
      setSearchParams({});
      toast({
        title: 'Setup Incomplete',
        description: 'Please complete your payout setup to enable redemptions.',
        status: 'warning',
        duration: 5000,
      });
    }
  }, [searchParams, currentUser?.uid]);

  // Crypto checkout in progress. The payment page opens in its own tab and
  // the app waits here: every 15s the server checks the order against
  // NOWPayments and credits it when paid, so nothing depends on the hosted
  // page sending the player back. Survives a refresh via sessionStorage.
  const [pendingInvoice, setPendingInvoice] = useState<{ orderId: string; url: string; usd?: number } | null>(null);
  const [checkingPurchase, setCheckingPurchase] = useState(false);
  const checkPurchase = async (orderId?: string): Promise<boolean> => {
    setCheckingPurchase(true);
    try {
      const r = await kycService.reconcileCryptoOrders(orderId);
      await loadWalletData();
      if ('credited' in r && r.credited) {
        try { sessionStorage.removeItem('pss:cryptoInvoice'); } catch { /* ignore */ }
        setPendingInvoice(null);
        toast({ title: 'Coins added', description: 'Your purchase is in your wallet.', status: 'success', duration: 6000 });
        return true;
      }
      const row = 'results' in r ? r.results.find((x) => !orderId || x.orderId === orderId) : null;
      if (row && ['failed', 'expired', 'refunded'].includes(row.paymentStatus || '')) {
        try { sessionStorage.removeItem('pss:cryptoInvoice'); } catch { /* ignore */ }
        setPendingInvoice(null);
        toast({ title: 'Payment not completed', description: `The payment page reported it as ${row.paymentStatus}. Start a new purchase when you are ready.`, status: 'warning', duration: 8000 });
      }
      return false;
    } finally {
      setCheckingPurchase(false);
    }
  };
  useEffect(() => {
    if (!currentUser?.uid) return;
    const purchase = searchParams.get('purchase');
    let stored: any = null;
    try { const raw = sessionStorage.getItem('pss:cryptoInvoice'); stored = raw ? JSON.parse(raw) : null; } catch { /* ignore */ }
    if (stored && Date.now() - (stored.ts || 0) > 24 * 60 * 60 * 1000) { stored = null; try { sessionStorage.removeItem('pss:cryptoInvoice'); } catch { /* ignore */ } }
    if (purchase !== 'pending' && purchase !== 'success' && !stored) return;
    const orderId = searchParams.get('order') || stored?.orderId || undefined;
    if (purchase) setSearchParams({});
    if (stored?.url) setPendingInvoice({ orderId: stored.orderId, url: stored.url, usd: stored.usd });
    let n = 0;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      n++;
      const done = await checkPurchase(orderId);
      if (done || stopped) return;
      // 15s for the first 10 minutes, then once a minute for an hour.
      if (n < 40) setTimeout(tick, 15000);
      else if (n < 100) setTimeout(tick, 60000);
    };
    tick();
    return () => { stopped = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser?.uid]);

  // Back from Didit (?verification=complete). Pull the decision from Didit
  // right away instead of waiting on the webhook, then re-read status a few
  // times in case the decision is still settling.
  useEffect(() => {
    if (searchParams.get('verification') !== 'complete' || !currentUser?.uid) return;
    const sessionId = searchParams.get('verificationSessionId');
    const hinted = (searchParams.get('status') || '').toLowerCase();
    setSearchParams({});
    setVerificationPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts++;
      const r = await kycService.syncSession(sessionId);
      await loadKYCStatus();
      const st = 'status' in r ? r.status : 'pending';
      if (st === 'verified' || st === 'rejected' || attempts >= 6) {
        setVerificationPolling(false);
        toast({
          title: st === 'verified' ? 'Identity verified' : st === 'rejected' ? 'Verification not approved' : 'Verification in review',
          description: st === 'verified' ? 'You can redeem prizes now.' : st === 'rejected' ? 'Check the reason in your Wallet or contact support.' : hinted === 'approved' ? 'Didit approved it; the result will show here in a moment.' : 'We will update your status as soon as Didit finishes.',
          status: st === 'verified' ? 'success' : st === 'rejected' ? 'error' : 'info', duration: 5000,
        });
        return;
      }
      setTimeout(tick, 3000);
    };
    tick();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, currentUser?.uid]);

  //  NEW: Poll Connect status until verified or timeout

  const pollConnectStatus = async () => {
    if (!currentUser?.uid) return;
    
    const maxAttempts = 15; // Poll for up to 30 seconds (15 x 2s)
    let attempts = 0;
    
    const poll = async () => {
      attempts++;
      
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `https://getconnectaccountstatus-efcly6byiq-uc.a.run.app?userId=${currentUser.uid}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          
          setConnectStatus({
            hasAccount: true,
            payoutsEnabled: data.payoutsEnabled || false,
            loading: false,
            accountStatus: data.accountStatus,
            ageRestricted: data.accountStatus === 'age_restricted',
            payoutMethods: data.payoutMethods, //  NEW: Capture payout methods

          });
          
          //  Auto-select payout method based on what's available

          if (data.payoutMethods) {
            if (!data.payoutMethods.standardPayoutEligible && data.payoutMethods.instantPayoutEligible) {
            }
          }
          
          // Check if fully verified
          if (data.payoutsEnabled) {
            setVerificationPolling(false);
            toast({
              title: 'Verification Complete!',

              description: 'Your account is now verified. You can redeem prizes.',
              status: 'success',
              duration: 5000,
            });
            
            // Reload KYC status too
            loadKYCStatus();
            return; // Stop polling
          }
          
          // Check if age restricted
          if (data.accountStatus === 'age_restricted') {
            setVerificationPolling(false);
            toast({
              title: 'Age Verification Failed',

              description: 'You must be 18 or older to use this service.',
              status: 'error',
              duration: 10000,
            });
            return; // Stop polling
          }
        }
      } catch (error) {
        console.error('Error polling connect status:', error);
      }
      
      // Continue polling if not verified yet
      if (attempts < maxAttempts) {
        setTimeout(poll, 2000); // Poll every 2 seconds
      } else {
        // Timeout - verification taking too long
        setVerificationPolling(false);
        toast({
          title: 'Verification Pending',
          description: 'Your verification is being processed. This may take a few minutes. Refresh the page to check status.',
          status: 'info',
          duration: 10000,
        });
      }
    };
    
    poll();
  };

  const loadWalletData = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      const [walletData, transactionData] = await Promise.all([
        walletService.getBothBalances(currentUser.uid),
        walletService.getTransactionHistory(currentUser.uid, 20),
      ]);

      setWallet(walletData || initialWallet);
      setTransactions(transactionData || []);
    } catch (error) {
      console.error('Error loading wallet data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load wallet data',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  //  Load ALL transactions (lifetime history)

  const loadAllTransactions = async () => {
    if (!currentUser?.uid) return;
    setLoadingAllTransactions(true);
    try {
      // Load up to 500 transactions for full history
      const allTx = await walletService.getTransactionHistory(currentUser.uid, 500);
      setAllTransactions(allTx || []);
    } catch (error) {
      console.error('Error loading all transactions:', error);
    } finally {
      setLoadingAllTransactions(false);
    }
  };

  //  Filter transactions based on selected filter

  // Per-hand chip movement records (win/loss every single hand at a poker
  // table - identified by tableId/handNumber; slots also use game_win/loss
  // but ARE real wallet movements and stay in All). These flooded the
  // default feed - a long session generates hundreds - so the 'All' view
  // hides them and they get their own filter.
  const isHandActivity = (tx: any) =>
    (tx.type === 'game_win' || tx.type === 'game_loss')
    && (!!(tx as any).tableId || (tx as any).handNumber != null);
  const isTournamentActivity = (tx: any) =>
    typeof tx.type === 'string'
    && (tx.type.toLowerCase().startsWith('tournament') || tx.type === 'Tournament cancelled');

  const getFilteredTransactions = () => {
    const txList = showAllTransactions ? allTransactions : transactions;
    if (transactionFilter === 'all') return txList.filter(tx => !isHandActivity(tx));

    return txList.filter(tx => {
      switch (transactionFilter) {
        case 'deposits':
          return tx.type === 'deposit' || tx.type === 'purchase';
        case 'withdrawals':
          return tx.type === 'withdrawal' || tx.type === 'redemption' || tx.type === 'payout';
        case 'games':
          return tx.type === 'table_join' || tx.type === 'table_leave' || tx.type === 'win' || tx.type === 'loss' || tx.type === 'rake';
        case 'tournaments':
          return isTournamentActivity(tx);
        case 'hands':
          return isHandActivity(tx);
        case 'bonuses':
          return tx.type === 'bonus' || tx.type === 'referral' || tx.type === 'daily_bonus' || tx.type === 'challenge_reward';
        case 'transfers':
          return tx.type === 'transfer_in' || tx.type === 'transfer_out' || tx.type === 'agent_transfer';
        default:
          return true;
      }
    });
  };

  //  NEW: Load KYC status

  const loadKYCStatus = async () => {
    if (!currentUser?.uid) return;
    try {
      const kycData = await complianceService.getKYCStatus(currentUser.uid);
      setKycStatus(kycData?.status || 'none');
    } catch (error) {
      console.error('Error loading KYC status:', error);
      setKycStatus('none');
    }
  };

  //  NEW: Check geo location

  const checkGeoLocation = async () => {
    try {
      const geoResult = await geoService.checkIP();
      if (geoResult) {
        // Always store the location info including region (state code)
        setGeoLocation({
          city: geoResult.city || 'Unknown',
          region: geoResult.region || 'Unknown',
          country: geoResult.country || 'Unknown',
        });
        
        if (!geoResult.allowed) {
          setGeoBlockReason(geoResult.reason || 'RESTRICTED_STATE');
        }
      }
    } catch (error) {
      console.error('Error checking geo:', error);
    }
  };

  //  Load Connect account status

  const loadConnectStatus = async () => {
    if (!currentUser?.uid) return;
    try {
      //  FIX: Get auth token for secure request

      const token = await currentUser.getIdToken();
      
      const response = await fetch(
        `https://getconnectaccountstatus-efcly6byiq-uc.a.run.app?userId=${currentUser.uid}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setConnectStatus({
          hasAccount: true,
          payoutsEnabled: data.payoutsEnabled || false,
          loading: false,
          accountStatus: data.accountStatus,
          ageRestricted: data.accountStatus === 'age_restricted',
          payoutMethods: data.payoutMethods, //  NEW: Capture payout methods

        });
        
        //  Auto-select payout method based on what's available

        if (data.payoutMethods) {
          if (!data.payoutMethods.standardPayoutEligible && data.payoutMethods.instantPayoutEligible) {
          }
        }
      } else {
        setConnectStatus({ hasAccount: false, payoutsEnabled: false, loading: false });
      }
    } catch (error) {
      console.error('Error loading connect status:', error);
      setConnectStatus({ hasAccount: false, payoutsEnabled: false, loading: false });
    }
  };

  //  Setup Connect account (bank info)

  const setupConnectAccount = async () => {
    if (!currentUser?.uid || !currentUser?.email) return;
    setSettingUpConnect(true);
    try {
      //  FIX: Get auth token for backend authentication

      const token = await currentUser.getIdToken();
      
      const response = await fetch('https://createconnectaccount-efcly6byiq-uc.a.run.app', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, //  FIX: Include auth token

        },
        body: JSON.stringify({
          email: currentUser.email, //  userId now comes from verified token on backend

          returnUrl: getStripeRedirectUrl('/wallet?connect=complete'),
          refreshUrl: getStripeRedirectUrl('/wallet?connect=refresh'),
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (error) {
      console.error('Error setting up Connect:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to start bank account setup',
        status: 'error',
        duration: 3000,
      });
      setSettingUpConnect(false);
    }
  };

  //  NEW: Handle AMOE claim

  const handleAMOEClaimed = async () => {
    await loadWalletData();
    toast({
      title: 'Free PSS Coins Claimed!',

      description: 'Your free PSS Coins has been added to your wallet.',
      status: 'success',
      duration: 4000,
    });
  };

  const handleWithdraw = async () => {
    if (!currentUser?.uid || !withdrawAmount) return;
    if (cashOutsPaused) {
      toast({ title: 'Redemptions are paused', description: paymentsHold.message, status: 'warning', duration: 6000 });
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Please enter a valid amount',
        status: 'error',
        duration: 2000,
      });
      return;
    }

    // Identity first, then a payout destination on file.
    if (kycStatus !== 'verified') {
      toast({ title: 'Prize eligibility check required', description: 'Confirm your age and state once; every redemption after that is quick.', status: 'info', duration: 4000 });
      if (isStoreSafe()) { onClose(); navigate('/eligibility'); } else { onKYCOpen(); }
      return;
    }
    const dest = payoutDests[payoutMethod];
    if (!dest || !dest.value) {
      toast({ title: 'Add where to send it', description: payoutMethod === 'usdc' ? 'Save your USDC wallet address first.' : 'Save the email or phone on your PayPal account first.', status: 'warning', duration: 4000 });
      return;
    }
    if (dest.holdUntil && new Date(dest.holdUntil).getTime() > Date.now()) {
      toast({ title: 'Wallet address on hold', description: `New addresses can receive payouts after ${new Date(dest.holdUntil).toLocaleString()}.`, status: 'warning', duration: 5000 });
      return;
    }

    //  NEW: Geo check before redemption

    if (geoBlockReason) {
      onGeoBlockedOpen();
      return;
    }

    //  CRITICAL: Double-check geo restriction in real-time before allowing withdrawal

    try {
      const currentGeo = await geoService.checkIP();
      if (currentGeo && !currentGeo.allowed) {
        toast({
          title: 'Redemption Blocked',

          description: currentGeo.reason || 'Redemptions are not available in your location.',
          status: 'error',
          duration: 5000,
        });
        setGeoBlockReason(currentGeo.reason || 'RESTRICTED_STATE');
        onGeoBlockedOpen();
        return;
      }
    } catch (geoError) {
      console.error('Geo check failed:', geoError);
      // If geo check fails, block withdrawal to be safe
      toast({
        title: 'Location Verification Failed',
        description: 'Unable to verify your location. Please try again.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    //  CRITICAL: Check withdrawable amount (total - playthrough requirement)

    const withdrawableAmount = Math.max(0, (wallet.pkCash || 0) - (wallet.pkCashPlaythrough || 0));
    
    if (selectedCurrency === 'pkcash' && amount > withdrawableAmount) {
      const playthroughRemaining = wallet.pkCashPlaythrough || 0;
      if (playthroughRemaining > 0) {
        toast({
          title: 'Playthrough Required',
          description: `You need to play through $${playthroughRemaining.toFixed(2)} more before redeeming. Redeemable: $${withdrawableAmount.toFixed(2)}`,
          status: 'warning',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Insufficient PSS Coins',
          description: `You only have $${wallet.pkCash.toFixed(2)} PSS Coins`,
          status: 'error',
          duration: 2000,
        });
      }
      return;
    }

    if (amount < 10) {
      toast({
        title: 'Minimum $10',
        description: 'Minimum redemption amount is $10',
        status: 'error',
        duration: 2000,
      });
      return;
    }

    try {
      setProcessing(true);

      //  FIX: Call requestPayout API endpoint (creates redemption for admin approval)

      const token = await currentUser.getIdToken();
      
      //  CRITICAL: Get current state code for geo restriction check

      const currentGeo = await geoService.checkIP();
      const stateCode = currentGeo?.region || geoLocation?.region;
      
      const response = await fetch(`${API_BASE}/requestPayout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          amount,
          payoutMethod, // usdc | paypal (destination saved on the account)

          stateCode, //  CRITICAL: Pass state code for geo restriction check

        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request redemption');
      }

      toast({
        title: 'Redemption Requested! ',

        description: `Your redemption of $${amount.toFixed(2)} is pending review. ${payoutMethod === 'usdc' ? 'USDC arrives minutes after approval.' : 'Sent by PayPal within 1 to 3 business days of approval.'}`,
        status: 'success',
        duration: 3000,
      });

      setWithdrawAmount('');
      onClose();
      await loadWalletData();
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to process redemption',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  //  Helper to format transaction date

  const formatDate = (tx: Transaction): string => {
    try {
      // Handle Firestore Timestamp
      if (tx.timestamp?.toDate) {
        return tx.timestamp.toDate().toLocaleDateString();
      }
      // Handle Date object
      if (tx.timestamp instanceof Date) {
        return tx.timestamp.toLocaleDateString();
      }
      // Handle string date
      if (tx.timestamp) {
        return new Date(tx.timestamp).toLocaleDateString();
      }
      // Fallback to createdAt
      if (tx.createdAt) {
        return new Date(tx.createdAt).toLocaleDateString();
      }
      return 'Recent';
    } catch {
      return 'Recent';
    }
  };

  //  Helper to get transaction icon

  const getTransactionIcon = (type: string): string => {
    switch (type) {
      case 'daily_bonus': return '';

      case 'game_win': return '';

      case 'game_loss': return '';

      case 'deposit': return '';

      case 'purchase': return '';

      case 'withdrawal': return '';

      case 'rake_earned': return '';

      case 'buy_in': return '';

      case 'cash_out': return '';

      default: return '';

    }
  };

  //  Helper to get transaction display name

  const getTransactionName = (type: string): string => {
    switch (type) {
      case 'daily_bonus': return 'Daily Bonus';
      case 'game_win': return 'Game Win';
      case 'game_loss': return 'Game Loss';
      case 'deposit': return 'Purchase';
      case 'purchase': return 'Purchase';
      case 'withdrawal': return 'Redemption';
      case 'rake_earned': return 'Rake Earned';
      case 'buy_in': return 'Table Buy-In';
      case 'cash_out': return 'Table Cash-Out';
      default: return type;
    }
  };

  //  FIXED: Helper to get amount color - now uses actual amount value

  const getAmountColor = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || amount >= 0) {
      return '#10b981'; // Green for positive/null
    } else {
      return '#ef4444'; // Red for negative
    }
  };

  //  FIXED: Helper to format amount with sign - uses actual database value

  const formatAmount = (tx: Transaction): string => {
    //  FIX: Handle null/undefined amounts

    if (tx.amount === null || tx.amount === undefined) {
      return '0';
    }
    // Use the actual sign from the database amount
    const sign = tx.amount > 0 ? '+' : '';
    // Format with proper decimals for PSS Coins
    if (tx.currency === 'pkcash') {
      return `${sign}${tx.amount.toFixed(2)}`;
    }
    // Use locale string for playcoins (whole numbers)
    return `${sign}${tx.amount.toLocaleString()}`;
  };

  //  NEW: Render KYC status badge - also checks Connect status

  const renderKYCBadge = () => {
    // iPhone: eligibility lives under Profile, never in the wallet
    if (isStoreSafe()) return null;
    //  NEW: Show verifying state when polling

    if (verificationPolling) {
      return (
        <Badge colorScheme="blue" display="flex" alignItems="center" gap={1}>
          <Spinner size="xs" />
          Verifying...
        </Badge>
      );
    }
    
    switch (kycStatus) {
      case 'verified':
        return <Badge colorScheme="green"><Shield size={10} style={{ display: 'inline', marginRight: 4 }} />Verified</Badge>;
      case 'pending':
        return <Badge colorScheme="yellow" cursor="pointer" onClick={onKYCOpen}>Pending</Badge>;
      case 'requires_input':
        return <Badge colorScheme="orange" cursor="pointer" onClick={onKYCOpen}>Action Required</Badge>;
      case 'rejected':
        return <Badge colorScheme="red" cursor="pointer" onClick={onKYCOpen}>Rejected</Badge>;
      default:
        return <Badge colorScheme="gray" cursor="pointer" onClick={onKYCOpen}>Not Verified</Badge>;
    }
  };

  if (loading) {
    return (
      <Container maxW="1200px" py={8}>
        <VStack spacing={4} align="center" justify="center" minH="50vh">
          <Text color={POKER_COLORS.cyan}>Loading wallet...</Text>
        </VStack>
      </Container>
    );
  }

  // Invited player who hit "Get coins" from a buy-in screen: bring them
  // straight back to their table (invite included) once they top up.
  let returnToTable: { url: string; ts: number } | null = null;
  try {
    const raw = localStorage.getItem('pss:returnToTable');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.url && Date.now() - (parsed.ts || 0) < 2 * 60 * 60 * 1000) returnToTable = parsed;
      else localStorage.removeItem('pss:returnToTable');
    }
  } catch { /* ignore */ }

  return (
    <Container maxW="1200px" py={8} px={{ base: 4, md: 8 }}>
      <VStack spacing={8} align="stretch">
        {returnToTable && (
          <HStack
            bg="rgba(0,217,217,0.10)"
            border="2px solid rgba(0,217,217,0.5)"
            borderRadius="xl"
            px={4} py={3}
            justify="space-between"
          >
            <Text color="cyan.200" fontSize="sm" fontWeight="600">
              Your seat is waiting. Head back when you are topped up.
            </Text>
            <Button size="sm" colorScheme="cyan"
              onClick={() => {
                const url = returnToTable!.url;
                try { localStorage.removeItem('pss:returnToTable'); } catch { /* ignore */ }
                navigate(url);
              }}>
              Return to table
            </Button>
          </HStack>
        )}
        {/* Header */}
        <HStack justify="space-between" align="start" flexWrap="wrap" rowGap={3}>
          <Box>
            <Heading
              size="lg"
              color={POKER_COLORS.cyan}
              mb={2}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <DollarSign size={28} />
              Wallet
            </Heading>
            <Text color={POKER_COLORS.accentCyan}>Manage your funds</Text>
          </Box>
          <HStack spacing={3}>
            {/*  NEW: KYC Status Badge */}

            {renderKYCBadge()}
            <PokerButton variant="primary" onClick={() => navigate('/buy-coins')} data-tutorial="buy-coins">
              + Buy PlayCoins
            </PokerButton>
          </HStack>
        </HStack>

        {pendingInvoice && (
          <Alert status="info" borderRadius="md" bg="blue.900" borderColor="blue.500" borderWidth="1px" alignItems="flex-start">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Waiting for your crypto payment{pendingInvoice.usd ? ` ($${Number(pendingInvoice.usd).toFixed(2)})` : ''}</AlertTitle>
              <AlertDescription fontSize="sm" display="block">
                Finish the payment in the tab that opened. Your coins are added here automatically once the network confirms it, usually within a few minutes. You can leave this page; we keep checking when you come back.
              </AlertDescription>
              <HStack mt={3} spacing={3} flexWrap="wrap">
                <Button size="sm" colorScheme="cyan" onClick={() => window.open(pendingInvoice.url, 'pss-checkout', 'popup=yes,width=560,height=900')}>Open payment page</Button>
                <Button size="sm" variant="outline" colorScheme="cyan" isLoading={checkingPurchase} onClick={() => checkPurchase(pendingInvoice.orderId)}>Check now</Button>
                <Button size="sm" variant="ghost" color="gray.400" onClick={() => { try { sessionStorage.removeItem('pss:cryptoInvoice'); } catch { /* ignore */ } setPendingInvoice(null); }}>Dismiss</Button>
              </HStack>
            </Box>
          </Alert>
        )}

        {/*  NEW: Verification In Progress Banner */}

        {verificationPolling && (
          <Alert 
            status="info" 
            borderRadius="md" 
            bg="blue.900" 
            borderColor="blue.500" 
            borderWidth="1px"
          >
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>⏳ Verifying Your Account...</AlertTitle>
              <AlertDescription fontSize="sm">
                We're checking your verification result. This usually takes just a few seconds.
                Please wait...
              </AlertDescription>
            </Box>
            <Spinner size="md" color="blue.300" ml={4} />
          </Alert>
        )}

        {/*  AMOE Claim Card - Shows when PSS Coins is below $1 (AMOE requirement) */}

        {currentUser?.uid && wallet.pkCash < 1 && (
          <AMOEClaimCard
            userId={currentUser.uid}
            pkCashBalance={wallet.pkCash}
            onClaimed={handleAMOEClaimed}
          />
        )}

        {/* Balance Cards */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} data-tutorial="wallet-balance">
          {/* PlayCoins Balance */}
          <PokerCard p={6}>
            <VStack align="start" spacing={4}>
              <HStack justify="space-between" w="100%">
                <Heading size="md" color={POKER_COLORS.cyan}>
                  PlayCoins
                </Heading>
                <Badge bg={POKER_COLORS.darkGreen} color="white">
                  FREE
                </Badge>
              </HStack>
              <Divider borderColor={POKER_COLORS.cyan} />
              <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                Non-redeemable play currency
              </Text>
              <Heading size="lg" color={POKER_COLORS.gold}>
                {wallet.playCoins.toLocaleString()}
              </Heading>
              <HStack spacing={4} fontSize="xs" color={POKER_COLORS.accentCyan}>
                {/*  ENTERPRISE: Show NET instead of separate Won/Lost */}

                {(() => {
                  const net = (wallet.totalPlayCoinsWon || 0) - (wallet.totalPlayCoinsLost || 0);
                  const isPositive = net >= 0;
                  return (
                    <HStack>
                      {isPositive ? <TrendingUp size={14} color="#10b981" /> : <TrendingDown size={14} color="#ef4444" />}
                      <Text color={isPositive ? '#10b981' : '#ef4444'}>
                        Net: {isPositive ? '+' : ''}{net.toLocaleString()}
                      </Text>
                    </HStack>
                  );
                })()}
              </HStack>
            </VStack>
          </PokerCard>

          {/* PSS Coins Balance */}
          <PokerCard p={6}>
            <VStack align="start" spacing={4}>
              <HStack justify="space-between" w="100%">
                <Heading size="md" color={POKER_COLORS.cyan}>
                  PSS Coins
                </Heading>
                <Badge bg={POKER_COLORS.gold} color={POKER_COLORS.darkTeal}>
                  REDEEMABLE
                </Badge>
              </HStack>
              <Divider borderColor={POKER_COLORS.cyan} />
              <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                Redeemable for prizes
              </Text>
              <Heading size="lg" color={POKER_COLORS.gold}>
                ${wallet.pkCash.toFixed(2)}
              </Heading>
              {/*  CRITICAL: Show withdrawable vs playthrough */}

              {(wallet.pkCashPlaythrough || 0) > 0 && (
                <Box w="100%" p={2} bg="orange.900" borderRadius="md" border="1px solid" borderColor="orange.500">
                  <VStack spacing={1} align="start">
                    <HStack justify="space-between" w="100%">
                      <Text color="orange.300" fontSize="xs" fontWeight="bold"> Playthrough Required</Text>

                    </HStack>
                    <HStack justify="space-between" w="100%" fontSize="xs">
                      <Text color="gray.400">Redeemable:</Text>
                      <Text color="green.400" fontWeight="bold">
                        ${Math.max(0, (wallet.pkCash || 0) - (wallet.pkCashPlaythrough || 0)).toFixed(2)}
                      </Text>
                    </HStack>
                    <HStack justify="space-between" w="100%" fontSize="xs">
                      <Text color="gray.400">Play through:</Text>
                      <Text color="orange.400">
                        ${(wallet.pkCashPlaythrough || 0).toFixed(2)}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>
              )}
              {(wallet.pkCashHeld || 0) > 0 && (
                <HStack fontSize="xs" color="yellow.400">
                  <Text>⏳ Pending redemption: ${(wallet.pkCashHeld || 0).toFixed(2)}</Text>
                </HStack>
              )}
              <HStack spacing={4} fontSize="xs" color={POKER_COLORS.accentCyan}>
                {/*  ENTERPRISE: Show NET instead of separate Won/Lost */}

                {(() => {
                  const net = (wallet.totalPKCashWon || 0) - (wallet.totalPKCashLost || 0);
                  const isPositive = net >= 0;
                  return (
                    <HStack>
                      {isPositive ? <TrendingUp size={14} color="#10b981" /> : <TrendingDown size={14} color="#ef4444" />}
                      <Text color={isPositive ? '#10b981' : '#ef4444'}>
                        Net: {isPositive ? '+' : ''}${net.toFixed(2)}
                      </Text>
                    </HStack>
                  );
                })()}
              </HStack>
            </VStack>
          </PokerCard>
          
          {/*  ENTERPRISE: Agent Rake Balance - Only shown if user has rake earnings */}

          {(wallet.rakeBalance || 0) > 0 && (
            <PokerCard p={6} borderColor="purple.500" borderWidth="2px">
              <VStack align="start" spacing={4}>
                <HStack justify="space-between" w="100%">
                  <Heading size="md" color="purple.400">
                     Agent Commissions

                  </Heading>
                  <Badge bg="purple.500" color="white">
                    AGENT
                  </Badge>
                </HStack>
                <Divider borderColor="purple.500" />
                <Text color="purple.300" fontSize="sm">
                  Rake earnings from referrals - request payout in Agent Dashboard
                </Text>
                <Heading size="lg" color={POKER_COLORS.gold}>
                  ${(wallet.rakeBalance || 0).toFixed(2)}
                </Heading>
                <Text color="gray.400" fontSize="xs">
                  This balance is separate from PSS Coins and cannot be used at tables.
                  Visit your Agent Dashboard to request a payout.
                </Text>
              </VStack>
            </PokerCard>
          )}
        </SimpleGrid>

        {/* Action Buttons - YOUR ORIGINAL + Rules + Send */}
        <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
          <PokerButton variant="secondary" onClick={() => setShowTransferModal(true)} data-tutorial="transfer">
             Send

          </PokerButton>
          <PokerButton variant="secondary" onClick={onOpen} data-tutorial="withdraw">
             Redeem

          </PokerButton>
          <PokerButton variant="secondary" onClick={() => navigate('/tables')}>
             Play

          </PokerButton>
          <PokerButton variant="secondary" onClick={() => navigate('/referrals')} data-tutorial="referrals">
             Referrals

          </PokerButton>
          <PokerButton variant="secondary" onClick={() => navigate('/legal?tab=rules')}>
             Rules

          </PokerButton>
        </SimpleGrid>

        {/* Withdrawal Modal -  ENHANCED with KYC + Bank Account + Payout Method */}

        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg={POKER_COLORS.darkTeal} borderColor={POKER_COLORS.cyan} border="2px">
            <ModalHeader color={POKER_COLORS.cyan}>Redeem prizes</ModalHeader>
            <ModalCloseButton color={POKER_COLORS.cyan} />

            <ModalBody>
              <VStack spacing={4}>
                {cashOutsPaused && (
                  <Alert status="warning" bg="rgba(249, 115, 22, 0.15)" border="1px solid #fb923c" borderRadius="md" alignItems="flex-start">
                    <AlertIcon color="#fb923c" />
                    <AlertDescription color="white" fontSize="sm">
                      <Text fontWeight="bold" mb={1}>Redemptions are paused right now</Text>
                      {paymentsHold.message}
                    </AlertDescription>
                  </Alert>
                )}
                {!isStoreSafe() && (<Alert status="info" bg="rgba(0,217,217,0.06)" border="1px solid rgba(0,217,217,0.3)" borderRadius="md" alignItems="flex-start">
                  <AlertIcon color="cyan.300" />
                  <AlertDescription color="gray.200" fontSize="xs" lineHeight="1.5">
                    <Text as="span" fontWeight="bold" color="white">Temporary redemption options.</Text> While we bring a new payment provider on board, PSS Coin redemptions are sent as USDC to your wallet or by PayPal. Bank transfer returns once the new provider is live. Your balance is unaffected.
                  </AlertDescription>
                </Alert>)}
                {/* Fair Play notice - deterrent at the exact moment a farmer
                    would try to extract. All of it is real: nightly scans,
                    ledger provenance, identity verification. */}
                <Text color="gray.500" fontSize="xs" textAlign="center" lineHeight="1.5">
                  🛡 All redemptions pass automated play-pattern forensics, source-of-funds
                  ledger review, and government-ID verification before payout.
                </Text>
                {/*  Age Restriction Alert */}

                {connectStatus.ageRestricted && (
                  <Alert status="error" borderRadius="md" bg="red.900" borderColor="red.500" border="1px solid">
                    <AlertIcon color="red.400" />
                    <Box>
                      <AlertTitle color="red.200">Age Verification Failed</AlertTitle>
                      <AlertDescription fontSize="sm" color="red.300">
                        You must be 18 or older to redeem. Contact support if you believe this is an error.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {/* Identity, then where to send it */}
                {kycStatus !== 'verified' && (
                  <Alert status="info" borderRadius="md" bg="blue.900" borderColor="blue.500" border="1px solid">
                    <AlertIcon color="blue.400" />
                    <Box flex="1">
                      <AlertTitle color="blue.200">Confirm prize eligibility first</AlertTitle>
                      <AlertDescription fontSize="sm" color="blue.300">One-time check that you are 18+ and in an eligible state. Required before prizes can be redeemed.</AlertDescription>
                    </Box>
                    <Button size="sm" colorScheme="cyan" onClick={() => (isStoreSafe() ? (onClose(), navigate('/eligibility')) : onKYCOpen())} ml={2}>
                      {isStoreSafe() ? 'Open Profile' : 'Confirm'}
                    </Button>
                  </Alert>
                )}
                <Box w="100%" p={3} bg="#0a1929" borderRadius="8px" border="1px solid" borderColor="cyan.700">
                  <Text color="gray.300" fontSize="sm" mb={2} fontWeight="medium">Where should we send your redemption?</Text>
                  <HStack spacing={2} mb={3} flexWrap="wrap">
                    {(usdcPayoutsEnabled ? (['usdc', 'paypal'] as const) : (['paypal'] as const)).map(m => (
                      <Button key={m} size="sm" variant={payoutMethod === m ? 'solid' : 'outline'} colorScheme="cyan" onClick={() => { setPayoutMethod(m); setDestInput(''); }}>
                        {m === 'usdc' ? 'USDC (minutes)' : 'PayPal'}
                      </Button>
                    ))}
                  </HStack>
                  {payoutDests[payoutMethod]?.value ? (
                    <HStack justify="space-between" mb={2} flexWrap="wrap" gap={1}>
                      <Text color="white" fontSize="sm" fontFamily={payoutMethod === 'usdc' ? 'mono' : undefined} wordBreak="break-all">{payoutDests[payoutMethod].value}</Text>
                      {payoutDests[payoutMethod].holdUntil && new Date(payoutDests[payoutMethod].holdUntil!).getTime() > Date.now()
                        ? <Badge colorScheme="orange" fontSize="xs">Ready {new Date(payoutDests[payoutMethod].holdUntil!).toLocaleString()}</Badge>
                        : <Badge colorScheme="green" fontSize="xs">On file</Badge>}
                    </HStack>
                  ) : (
                    <Text color="gray.500" fontSize="xs" mb={2}>{payoutMethod === 'usdc' ? `Nothing saved yet. Redemptions are sent as ${usdcNetworkLabel}; use a wallet address on that network.` : 'No PayPal contact saved yet.'}</Text>
                  )}
                  <HStack>
                    <Input
                      size="sm"
                      placeholder={payoutMethod === 'usdc' ? 'Your USDC wallet address' : 'Email or phone on your PayPal'}
                      value={destInput}
                      onChange={(e) => setDestInput(e.target.value)}
                      bg="#06141F" borderColor="cyan.700" color="white" _placeholder={{ color: 'gray.500' }}
                    />
                    <Button size="sm" colorScheme="cyan" variant="outline" onClick={savePayoutDestination} isLoading={savingDest} isDisabled={!destInput.trim()}>Save</Button>
                  </HStack>
                  <Text color="gray.500" fontSize="xs" mt={2}>
                    {payoutMethod === 'usdc'
                      ? `Must be a wallet you control on ${usdcNetworkLabel}. A new address can receive redemptions 24 hours after it is saved. You pay only the network fee.`
                      : 'Must be the PayPal account in your own name. Sent within 1 to 3 business days of approval, no fee from us.'}
                  </Text>
                </Box>

                {/*  CRITICAL: Show playthrough requirement warning */}

                {(wallet.pkCashPlaythrough || 0) > 0 && (
                  <Alert status="warning" borderRadius="md" bg="orange.900" borderColor="orange.500" border="1px solid">
                    <AlertIcon color="orange.400" />
                    <Box>
                      <AlertTitle color="orange.200">Playthrough Required</AlertTitle>
                      <AlertDescription fontSize="sm" color="orange.300">
                        You must wager ${(wallet.pkCashPlaythrough || 0).toFixed(2)} more before redeeming bonus funds.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                <Box
                  w="100%"
                  p={4}
                  bg="#0a1929"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="cyan.600"
                >
                  <HStack justify="space-between" mb={2}>
                    <Text color="gray.300" fontSize="sm">Total Balance:</Text>
                    <Text color="white" fontSize="sm" fontWeight="medium">${wallet.pkCash.toFixed(2)}</Text>
                  </HStack>
                  {(wallet.pkCashPlaythrough || 0) > 0 && (
                    <HStack justify="space-between" mb={2}>
                      <Text color="orange.300" fontSize="sm">Playthrough Remaining:</Text>
                      <Text color="orange.200" fontSize="sm">-${(wallet.pkCashPlaythrough || 0).toFixed(2)}</Text>
                    </HStack>
                  )}
                  <Divider my={2} borderColor="cyan.700" />
                  <HStack justify="space-between">
                    <Text color="cyan.300" fontSize="sm" fontWeight="semibold">Redeemable:</Text>
                    <Heading size="md" color="cyan.400">
                      ${Math.max(0, (wallet.pkCash || 0) - (wallet.pkCashPlaythrough || 0)).toFixed(2)}
                    </Heading>
                  </HStack>
                </Box>

                <InputGroup>
                  <InputLeftAddon
                    bg="cyan.700"
                    borderColor="cyan.600"
                    color="white"
                    fontWeight="bold"
                  >
                    $
                  </InputLeftAddon>
                  <Input
                    placeholder="Enter amount"
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    bg="#0a1929"
                    borderColor="cyan.600"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ borderColor: 'cyan.400', boxShadow: '0 0 0 1px cyan' }}
                    max={Math.max(0, (wallet.pkCash || 0) - (wallet.pkCashPlaythrough || 0))}
                  />
                </InputGroup>

                <Text color="gray.400" fontSize="sm" textAlign="center">
                  Minimum redemption: $10
                </Text>
              </VStack>
            </ModalBody>

            <ModalFooter gap={3}>
              <PokerButton variant="secondary" onClick={onClose}>
                Cancel
              </PokerButton>
              <PokerButton
                variant="primary"
                onClick={handleWithdraw}
                isLoading={processing}
                isDisabled={kycStatus !== 'verified' || cashOutsPaused || !payoutDests[payoutMethod]?.value}
              >
                Redeem
              </PokerButton>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Transaction History */}
        <Box data-tutorial="transactions">
          <HStack justify="space-between" align="center" mb={4} flexWrap={{ base: 'wrap', md: 'nowrap' }} gap={2}>
            <Heading
              size={{ base: 'sm', md: 'md' }}
              color={POKER_COLORS.cyan}
              display="flex"
              alignItems="center"
              gap={2}
            >
              <History size={20} />
              {showAllTransactions ? 'All Transactions' : 'Recent Transactions'}
            </Heading>
            <Button
              size="sm"
              variant="outline"
              colorScheme="cyan"
              onClick={() => {
                if (!showAllTransactions && allTransactions.length === 0) {
                  loadAllTransactions();
                }
                setShowAllTransactions(!showAllTransactions);
              }}
              isLoading={loadingAllTransactions}
              flexShrink={0}
            >
              {showAllTransactions ? 'Show Recent' : 'View All'}
            </Button>
          </HStack>

          {/* Filter Buttons - Scrollable on mobile */}
          <Box 
            overflowX="auto" 
            pb={2} 
            mb={4}
            css={{
              '&::-webkit-scrollbar': { height: '4px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: POKER_COLORS.cyan, borderRadius: '4px' },
            }}
          >
            <HStack spacing={2} flexWrap="wrap" rowGap={2}>
            {[
              { value: 'all', label: 'All' },
              { value: 'deposits', label: 'Purchases' },

              { value: 'withdrawals', label: 'Redemptions' },

              { value: 'games', label: 'Games' },

              { value: 'tournaments', label: 'Tournaments' },

              { value: 'hands', label: 'Hand Activity' },

              { value: 'bonuses', label: 'Bonuses' },

              { value: 'transfers', label: '↔ Transfers' },

            ].map(filter => (
              <Button
                key={filter.value}
                size="sm"
                variant={transactionFilter === filter.value ? 'solid' : 'outline'}
                bg={transactionFilter === filter.value ? POKER_COLORS.cyan : 'transparent'}
                color={transactionFilter === filter.value ? 'black' : 'white'}
                borderColor={POKER_COLORS.cyan}
                borderWidth="2px"
                _hover={{ 
                  bg: transactionFilter === filter.value ? POKER_COLORS.cyan : 'whiteAlpha.200',
                }}
                onClick={() => setTransactionFilter(filter.value)}
                flexShrink={0}
              >
                {filter.label}
              </Button>
            ))}
            </HStack>
          </Box>

          {getFilteredTransactions().length === 0 ? (
            <PokerCard p={8} textAlign="center">
              <VStack spacing={3}>
                <CreditCard size={32} color={POKER_COLORS.cyan} />
                <Text color={POKER_COLORS.accentCyan}>
                  {transactionFilter === 'all' ? 'No transactions yet' : `No ${transactionFilter} found`}
                </Text>
              </VStack>
            </PokerCard>
          ) : (
            <>
              <Text color="gray.500" fontSize="sm" mb={3}>
                Showing {getFilteredTransactions().length} transaction{getFilteredTransactions().length !== 1 ? 's' : ''}
              </Text>
              <SimpleGrid columns={1} spacing={3} maxH={showAllTransactions ? '70vh' : 'auto'} overflowY={showAllTransactions ? 'auto' : 'visible'} sx={{ WebkitOverflowScrolling: 'touch' }}>
                {getFilteredTransactions().slice(0, showAllTransactions ? 100 : 10).map((tx) => (
                  <PokerCard key={tx.id} p={4}>
                    <HStack justify="space-between" align="center">
                      {/* Left Side */}
                      <HStack spacing={3} flex={1}>
                        <Box
                          p={2}
                          bg={POKER_COLORS.darkBase}
                          borderRadius="50%"
                          fontSize="xl"
                        >
                          {getTransactionIcon(tx.type)}
                        </Box>
                        <VStack align="start" spacing={0}>
                          <Text color={POKER_COLORS.cyan} fontWeight="bold">
                            {getTransactionName(tx.type)}
                          </Text>
                          <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                            {formatDate(tx)}
                          </Text>
                          {tx.description && (
                            <Text color="gray.500" fontSize="xs" noOfLines={1}>
                              {tx.description}
                            </Text>
                          )}
                          {tx.tableName && (
                            <Text color="gray.500" fontSize="xs">
                               {tx.tableName}

                            </Text>
                          )}
                        </VStack>
                      </HStack>

                      {/* Right Side */}
                      <VStack align="end" spacing={0}>
                        <Text
                          color={getAmountColor(tx.amount)}
                          fontWeight="bold"
                          fontSize="lg"
                        >
                          {formatAmount(tx)}
                        </Text>
                        {tx.currency && (
                          <Badge
                            fontSize="xs"
                            bg={tx.currency === 'playcoins' ? POKER_COLORS.cyan : POKER_COLORS.gold}
                            color={POKER_COLORS.darkTeal}
                          >
                            {tx.currency === 'playcoins' ? 'PLAYCOINS' : 'PSS COINS'}
                          </Badge>
                        )}
                      </VStack>
                    </HStack>
                  </PokerCard>
                ))}
              </SimpleGrid>
              {showAllTransactions && getFilteredTransactions().length > 100 && (
                <Text color="gray.500" fontSize="sm" mt={3} textAlign="center">
                  Showing first 100 of {getFilteredTransactions().length} transactions
                </Text>
              )}
            </>
          )}
        </Box>

        {/*  NEW: KYC Verification Modal */}

        <KYCVerificationModal
          isOpen={isKYCOpen}
          onClose={onKYCClose}
          userId={currentUser?.uid || ''}
          currentStatus={kycStatus}
          onVerificationStarted={() => {
            toast({
              title: 'Verification Started',
              description: 'Complete the verification process in the new window.',
              status: 'info',
              duration: 5000,
            });
          }}
          onVerificationComplete={() => {
            loadKYCStatus();
            onKYCClose();
          }}
        />

        {/*  NEW: Geo Blocked Modal */}

        <GeoBlockedModal
          isOpen={isGeoBlockedOpen}
          onClose={onGeoBlockedClose}
          reason={geoBlockReason || 'RESTRICTED_STATE'}
          location={geoLocation || undefined}
          onPlayCoinsOnly={() => {
            onGeoBlockedClose();
            navigate('/tables');
          }}
        />

        {/*  NEW: Transfer Money Modal */}

        <TransferModal
          isOpen={showTransferModal}
          onClose={() => setShowTransferModal(false)}
          walletBalance={{
            playCoins: wallet.playCoins || 0,
            pkCash: wallet.pkCash || 0,
          }}
          isVerified={kycStatus === 'verified'}
          currentUser={currentUser}
          onSuccess={() => {
            loadWalletData(); // Refresh wallet after transfer
            toast({
              title: 'Transfer Sent! ',

              description: 'The recipient will be notified.',
              status: 'success',
              duration: 3000,
            });
          }}
        />
      </VStack>
    </Container>
  );
};

export default WalletPage;